import { useState } from "react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
} from "../components/ui/dialog";
import { submitLead } from "../lib/api";
import { ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * Compact inline contact form rendered inside a modal dialog.
 * Triggered from the footer mail icon.
 */
export default function ContactDialog({ children }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        message: "",
        interest: "contact",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Name, email and message are required");
            return;
        }
        try {
            setBusy(true);
            await submitLead(form);
            setDone(true);
            toast.success("Message sent — we'll be in touch soon.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setForm({ name: "", email: "", company: "", message: "", interest: "contact" });
        setDone(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                setOpen(v);
                if (!v) setTimeout(reset, 200);
            }}
        >
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent
                data-testid="contact-dialog"
                className="bg-[#14141f] border border-white/10 text-zinc-100 sm:max-w-md"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-white tracking-tight">
                        Send us a message
                    </DialogTitle>
                    <DialogDescription className="text-sm text-zinc-400 mt-1">
                        Goes directly to our inbox — we reply within one business day.
                    </DialogDescription>
                </DialogHeader>

                {done ? (
                    <div className="text-center py-6" data-testid="contact-dialog-success">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-cyan-400/40 bg-cyan-400/10 mb-4">
                            <CheckCircle2 size={24} className="text-cyan-300" />
                        </div>
                        <h3 className="font-display text-lg text-white">Message received</h3>
                        <p className="text-sm text-zinc-400 mt-1 max-w-xs mx-auto">
                            We&apos;ll reply within one business day — usually much sooner.
                        </p>
                        <button
                            onClick={() => setOpen(false)}
                            className="btn-secondary mt-5 mx-auto"
                            data-testid="contact-dialog-close"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="space-y-3 mt-2" data-testid="contact-dialog-form">
                        <Field label="Your name *" testid="cd-name">
                            <input
                                type="text"
                                value={form.name}
                                onChange={set("name")}
                                className="input-dark w-full rounded-lg px-4 py-2.5 text-sm"
                                placeholder="Jane Citizen"
                                required
                                data-testid="cd-input-name"
                            />
                        </Field>
                        <Field label="Email *" testid="cd-email">
                            <input
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                className="input-dark w-full rounded-lg px-4 py-2.5 text-sm"
                                placeholder="jane@company.com.au"
                                required
                                data-testid="cd-input-email"
                            />
                        </Field>
                        <Field label="Company" testid="cd-company">
                            <input
                                type="text"
                                value={form.company}
                                onChange={set("company")}
                                className="input-dark w-full rounded-lg px-4 py-2.5 text-sm"
                                placeholder="Your business (optional)"
                                data-testid="cd-input-company"
                            />
                        </Field>
                        <Field label="Message *" testid="cd-message">
                            <textarea
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                className="input-dark w-full rounded-lg px-4 py-2.5 text-sm resize-none"
                                placeholder="What's on your mind?"
                                required
                                data-testid="cd-input-message"
                            />
                        </Field>
                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={busy}
                                className="btn-primary w-full justify-center disabled:opacity-60"
                                data-testid="cd-submit"
                            >
                                {busy ? "Sending..." : "Send message"}
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, testid, children }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="block text-[11px] font-mono-accent text-zinc-400 mb-1.5 uppercase tracking-[0.18em]">
                {label}
            </span>
            {children}
        </label>
    );
}
